import React from 'react';
import Hero from '../components/ui/Hero';
import Section from '../components/ui/Section';
import Card from '../components/ui/Card';
import ContactForm from '../components/forms/ContactForm';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMail, FiPhone, FiMapPin, FiFacebook, FiInstagram, FiTwitter, FiYoutube } = FiIcons;

const Contact = () => {
  const contactInfo = [
    {
      icon: FiMail,
      title: 'Email Us',
      details: 'info@love180.org',
      description: 'General inquiries and ministry questions'
    },
    {
      icon: FiPhone,
      title: 'Call Us',
      details: '(555) 123-4567',
      description: 'Available Monday-Friday, 9 AM - 5 PM EST'
    },
    {
      icon: FiMapPin,
      title: 'Visit Us',
      details: 'Gainesville, Florida',
      description: 'Ministry headquarters and training center'
    }
  ];

  const socialLinks = [
    { icon: FiFacebook, name: 'Facebook', url: '#' },
    { icon: FiInstagram, name: 'Instagram', url: '#' },
    { icon: FiTwitter, name: 'Twitter', url: '#' },
    { icon: FiYoutube, name: 'YouTube', url: '#' }
  ];

  return (
    <div>
      <Hero
        title="Contact Love 180"
        description="We'd love to hear from you! Whether you have questions about our ministry, want to get involved, or need prayer, we're here to connect with you."
        backgroundImage="https://images.unsplash.com/photo-1516321318423-f06f85e504b3?ixlib=rb-4.0.3&auto=format&fit=crop&w=1950&q=80"
      />

      {/* Contact Information */}
      <Section>
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {contactInfo.map((info, index) => (
            <Card key={index} className="p-6 text-center">
              <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={info.icon} className="w-6 h-6 text-primary-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">{info.title}</h3>
              <p className="text-xl font-medium text-primary-600 mb-2">{info.details}</p>
              <p className="text-sm text-neutral-600">{info.description}</p>
            </Card>
          ))}
        </div>

        <div className="grid md:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div>
            <h2 className="text-2xl font-bold text-neutral-900 mb-6">Send Us a Message</h2>
            <ContactForm />
          </div>

          {/* Additional Information */}
          <div>
            <h2 className="text-2xl font-bold text-neutral-900 mb-6">Connect With Us</h2>
            
            <div className="space-y-6">
              <Card className="p-6">
                <h3 className="font-semibold mb-3">Ministry Office Hours</h3>
                <div className="space-y-2 text-sm text-neutral-600">
                  <div className="flex justify-between">
                    <span>Monday - Friday:</span>
                    <span>9:00 AM - 5:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Saturday:</span>
                    <span>By appointment</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Sunday:</span>
                    <span>Closed</span>
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <h3 className="font-semibold mb-3">Follow Our Ministry</h3>
                <p className="text-sm text-neutral-600 mb-4">
                  Stay updated with our latest outreach efforts, testimonies, and events.
                </p>
                <div className="flex space-x-4 justify-center sm:justify-start">
                  {socialLinks.map((social, index) => (
                    <a
                      key={index}
                      href={social.url}
                      className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center text-primary-600 hover:bg-primary-200 transition-colors"
                      aria-label={social.name}
                    >
                      <SafeIcon icon={social.icon} className="w-5 h-5" />
                    </a>
                  ))}
                </div>
              </Card>

              <Card className="p-6">
                <h3 className="font-semibold mb-3">Mailing Address</h3>
                <div className="text-sm text-neutral-600">
                  <p>Love 180 Ministry</p>
                  <p>PO Box 12345</p>
                  <p>Gainesville, FL 32601</p>
                </div>
              </Card>

              <Card className="p-6">
                <h3 className="font-semibold mb-3">Prayer Requests</h3>
                <p className="text-sm text-neutral-600">
                  Have a prayer request? Email us at{' '}
                  <a href="mailto:prayer@love180.org" className="text-primary-600 hover:underline">
                    prayer@love180.org
                  </a>{' '}
                  and our prayer team will lift you up in prayer.
                </p>
              </Card>
            </div>
          </div>
        </div>
      </Section>

      {/* Map Placeholder */}
      <Section background="gray">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-neutral-900 mb-4">Find Us in Florida</h2>
          <p className="text-lg text-neutral-600">
            Love 180 is headquartered in Gainesville with outreach teams across Florida.
          </p>
        </div>
        
        <Card className="p-8">
          <div className="bg-neutral-100 h-64 rounded-lg flex items-center justify-center">
            <div className="text-center">
              <SafeIcon icon={FiMapPin} className="w-12 h-12 text-primary-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-neutral-700 mb-2">Interactive Map</h3>
              <p className="text-neutral-600">
                Google Maps integration would be embedded here showing our location and outreach areas.
              </p>
            </div>
          </div>
        </Card>
      </Section>

      {/* Emergency Contact */}
      <Section>
        <div className="max-w-2xl mx-auto text-center">
          <Card className="p-6 bg-primary-50 border-primary-200">
            <h3 className="text-lg font-semibold text-primary-900 mb-3">
              Need Immediate Prayer or Support?
            </h3>
            <p className="text-primary-800 mb-4">
              If you're going through a crisis and need immediate prayer or support, please don't hesitate to reach out. 
              We have team members available to pray with you and provide spiritual encouragement.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href="tel:5551234567"
                className="inline-flex items-center justify-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                <SafeIcon icon={FiPhone} className="w-4 h-4 mr-2" />
                Call Prayer Line
              </a>
              <a
                href="mailto:prayer@love180.org"
                className="inline-flex items-center justify-center px-4 py-2 bg-white text-primary-600 border border-primary-600 rounded-lg hover:bg-primary-50 transition-colors"
              >
                <SafeIcon icon={FiMail} className="w-4 h-4 mr-2" />
                Email for Prayer
              </a>
            </div>
          </Card>
        </div>
      </Section>
    </div>
  );
};

export default Contact;